import React, { useState, useMemo } from 'react';
import { useClerk } from '@clerk/react';
import { Coins, Trash2, KeyRound, GitMerge, UserCog, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useFetch, fmtDate, fmtDateTime, roleStyles } from '../utils';
import { adminApi } from '../api';
import { Section } from '../_primitives/Section';
import { DataTable, type Column } from '../_primitives/DataTable';
import { Sheet } from '../_primitives/Sheet';
import { ConfirmDialog } from '../_primitives/ConfirmDialog';
import ImpersonateButton from '../../../src/components/admin/ImpersonateButton';
import type { AdminUser, Role } from '../types';

const cols = (onClick: (u: AdminUser) => void): Column<AdminUser>[] => [
  {
    key: 'email', label: 'Email',
    sortValue: (u) => u.email,
    searchValue: (u) => u.email,
    render: (u) => <span className="font-medium text-ink dark:text-white truncate max-w-[22ch] inline-block" title={u.email}>{u.email}</span>,
  },
  {
    key: 'first_name', label: 'Naam',
    sortValue: (u) => u.first_name ?? '',
    searchValue: (u) => u.first_name ?? '',
    render: (u) => <span className="text-ink/75 dark:text-white/75">{u.first_name ?? '—'}</span>,
  },
  {
    key: 'role', label: 'Rol',
    sortValue: (u) => u.role,
    render: (u) => <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap ${roleStyles[u.role] ?? roleStyles.medewerker}`}>{u.role}</span>,
  },
  {
    key: 'company_name', label: 'Bedrijf',
    sortValue: (u) => u.company_name ?? '',
    searchValue: (u) => u.company_name ?? '',
    render: (u) => <span className="text-ink/65 dark:text-white/65 truncate max-w-[20ch] inline-block" title={u.company_name ?? ''}>{u.company_name ?? '—'}</span>,
  },
  {
    key: 'credits', label: 'Credits', align: 'right',
    sortValue: (u) => u.credits,
    render: (u) => <span className="font-mono text-ink/75 dark:text-white/75">{u.credits}</span>,
  },
  {
    key: 'login_count', label: 'Logins', align: 'right',
    sortValue: (u) => u.login_count,
    render: (u) => <span className="font-mono text-ink/55 dark:text-white/55">{u.login_count}</span>,
  },
  {
    key: 'created_at', label: 'Geregistreerd',
    sortValue: (u) => u.created_at,
    render: (u) => <span className="text-ink/55 dark:text-white/55">{fmtDate(u.created_at)}</span>,
  },
  {
    key: 'last_login', label: 'Laatste login',
    sortValue: (u) => u.last_login ?? '',
    render: (u) => <span className="text-ink/55 dark:text-white/55">{fmtDate(u.last_login)}</span>,
  },
  {
    key: 'is_active', label: 'Status',
    sortValue: (u) => u.is_active,
    render: (u) => u.is_active ? (
      <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-md bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-900">actief</span>
    ) : (
      <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-md bg-black/5 dark:bg-white/10 text-ink/55 dark:text-white/55 border border-black/10 dark:border-white/15">inactief</span>
    ),
  },
  {
    key: '__impersonate', label: '',
    className: 'whitespace-nowrap w-px',
    thClassName: 'w-px',
    render: (u) => (
      <span onClick={(e) => e.stopPropagation()}>
        <ImpersonateButton targetUserId={u.id as number} targetEmail={u.email} />
      </span>
    ),
  },
  {
    key: '__actions', label: '',
    className: 'whitespace-nowrap w-px',
    thClassName: 'w-px',
    render: (u) => (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(u); }}
        className="text-[11px] font-semibold text-accent dark:text-accent hover:underline"
      >
        Open
      </button>
    ),
  },
];

interface Props {
  /** External trigger: parent palette can request a specific action panel. */
  trigger?: { kind: 'grant-credits' | 'set-role' | 'impersonate' | 'invite-user' | 'merge-duplicate' | 'bulk-delete' | null; nonce: number };
}

export const UsersTab: React.FC<Props> = ({ trigger }) => {
  const { data, loading, error, refresh } = useFetch(adminApi.users);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [drilldown, setDrilldown] = useState<AdminUser | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [showBulkCredits, setShowBulkCredits] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showImpersonate, setShowImpersonate] = useState(false);

  const rows = data ?? [];
  const selectedUsers = useMemo(() => rows.filter((u) => selected.has(u.id)), [rows, selected]);

  // React to ⌘K palette actions.
  React.useEffect(() => {
    if (!trigger?.kind) return;
    if (trigger.kind === 'grant-credits') setShowBulkCredits(true);
    if (trigger.kind === 'set-role') {
      if (selectedUsers.length === 1) setDrilldown(selectedUsers[0]);
      else toast.info('Selecteer eerst één gebruiker om de rol aan te passen.');
    }
    if (trigger.kind === 'impersonate') setShowImpersonate(true);
    if (trigger.kind === 'invite-user') setShowInvite(true);
    if (trigger.kind === 'merge-duplicate') setShowMerge(true);
    if (trigger.kind === 'bulk-delete') setShowBulkDelete(true);
  }, [trigger, selectedUsers]);

  const toggle = (k: string | number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const toggleAll = (keys: (string | number)[], allSelected: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
  };

  const toolbar = (
    <div className="flex items-center gap-2 flex-wrap">
      {selected.size > 0 && (
        <span className="text-xs font-semibold text-accent dark:text-accent">{selected.size} geselecteerd</span>
      )}
      <button type="button" onClick={() => setShowBulkCredits(true)} disabled={selected.size === 0}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-black/10 dark:border-white/15 hover:bg-black/[0.03] dark:hover:bg-white/5 text-xs font-semibold text-ink/75 dark:text-white/75 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        <Coins size={12} /> Credits…
      </button>
      <button type="button" onClick={() => setShowBulkDelete(true)} disabled={selected.size === 0}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-red-300 dark:border-red-900 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        <Trash2 size={12} /> Verwijderen…
      </button>
      <button type="button" onClick={() => setShowInvite(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-accent hover:bg-accent-hover text-white text-xs font-semibold transition-colors">
        <UserPlus size={12} /> Nieuwe invite
      </button>
    </div>
  );

  return (
    <>
      <Section title={loading ? 'Gebruikers' : `Gebruikers (${rows.length})`} toolbar={toolbar}>
        <DataTable
          rows={rows}
          loading={loading}
          error={error}
          onRetry={refresh}
          columns={cols(setDrilldown)}
          rowKey={(u) => u.id}
          searchPlaceholder="Zoek op email, naam of bedrijf…"
          selection={{ selected, onToggle: toggle, onToggleAll: toggleAll }}
        />
      </Section>

      <UserDrilldown user={drilldown} onClose={() => setDrilldown(null)} onChanged={refresh} />

      <BulkCreditsDialog
        open={showBulkCredits}
        userIds={Array.from(selected) as number[]}
        onClose={() => setShowBulkCredits(false)}
        onDone={() => { setShowBulkCredits(false); setSelected(new Set()); refresh(); }}
      />

      <BulkDeleteDialog
        open={showBulkDelete}
        users={selectedUsers}
        onClose={() => setShowBulkDelete(false)}
        onDone={() => { setShowBulkDelete(false); setSelected(new Set()); refresh(); }}
      />

      <InviteDialog open={showInvite} onClose={() => setShowInvite(false)} onDone={() => { setShowInvite(false); refresh(); }} />

      <MergeDuplicateDialog open={showMerge} onClose={() => setShowMerge(false)} onDone={() => { setShowMerge(false); refresh(); }} />

      <ImpersonateDialog
        open={showImpersonate}
        users={rows}
        onClose={() => setShowImpersonate(false)}
      />
    </>
  );
};

// ─── Drilldown ─────────────────────────────────────────────────────────

const UserDrilldown: React.FC<{ user: AdminUser | null; onClose: () => void; onChanged: () => void }> = ({ user, onClose, onChanged }) => {
  const [role, setRole] = useState<Role>('medewerker');
  const [busy, setBusy] = useState(false);
  const clerk = useClerk();

  React.useEffect(() => { if (user) setRole(user.role); }, [user]);

  if (!user) return <Sheet open={false} onClose={onClose}><div /></Sheet>;

  const setRoleNow = async () => {
    setBusy(true);
    try {
      await adminApi.setRole(user.id, role);
      toast.success(`Rol bijgewerkt naar ${role}`);
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Rol bijwerken mislukt');
    } finally { setBusy(false); }
  };

  const impersonateNow = async () => {
    if (!window.confirm(`Impersonate ${user.email}? Deze handeling wordt geaudit.`)) return;
    setBusy(true);
    try {
      const { token } = await adminApi.impersonate(user.id as number);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (clerk as any).client.signIn.create({ strategy: 'ticket', ticket: token });
      await clerk.setActive({ session: result.createdSessionId });
      window.location.href = '/';
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Impersonate mislukt');
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={!!user} onClose={onClose} title={user.first_name ?? user.email} subtitle={user.email}>
      <div className="p-5 space-y-5">
        <dl className="grid grid-cols-2 gap-3 text-xs">
          <div><dt className="text-ink/45 dark:text-white/45 uppercase tracking-wider font-semibold">ID</dt><dd className="font-mono mt-0.5">{user.id}</dd></div>
          <div><dt className="text-ink/45 dark:text-white/45 uppercase tracking-wider font-semibold">Credits</dt><dd className="font-mono mt-0.5">{user.credits}</dd></div>
          <div><dt className="text-ink/45 dark:text-white/45 uppercase tracking-wider font-semibold">Bedrijf</dt><dd className="mt-0.5">{user.company_name ?? '—'}</dd></div>
          <div><dt className="text-ink/45 dark:text-white/45 uppercase tracking-wider font-semibold">Plan</dt><dd className="mt-0.5">{user.subscription_plan ?? '—'}</dd></div>
          <div><dt className="text-ink/45 dark:text-white/45 uppercase tracking-wider font-semibold">Aangemaakt</dt><dd className="mt-0.5">{fmtDate(user.created_at)}</dd></div>
          <div><dt className="text-ink/45 dark:text-white/45 uppercase tracking-wider font-semibold">Laatste login</dt><dd className="mt-0.5">{fmtDateTime(user.last_login)}</dd></div>
        </dl>

        <div className="border-t border-black/8 dark:border-white/10 pt-4">
          <h4 className="text-xs uppercase tracking-wider font-semibold text-ink/55 dark:text-white/55 mb-2 flex items-center gap-1.5">
            <UserCog size={12} /> Rol instellen
          </h4>
          <div className="flex gap-2">
            {(['medewerker', 'beheerder', 'superadmin'] as Role[]).map((r) => (
              <label key={r} className={`flex-1 cursor-pointer inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border text-xs font-semibold transition-colors ${role === r ? 'border-accent bg-accent/10 text-accent' : 'border-black/10 dark:border-white/15 text-ink/65 dark:text-white/65 hover:bg-black/[0.03] dark:hover:bg-white/5'}`}>
                <input type="radio" className="sr-only" name="role" value={r} checked={role === r} onChange={() => setRole(r)} />
                {r}
              </label>
            ))}
          </div>
          <button type="button" onClick={setRoleNow} disabled={busy || role === user.role}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-accent hover:bg-accent-hover text-white text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            Rol bijwerken
          </button>
        </div>

        <div className="border-t border-black/8 dark:border-white/10 pt-4">
          <h4 className="text-xs uppercase tracking-wider font-semibold text-ink/55 dark:text-white/55 mb-2 flex items-center gap-1.5">
            <KeyRound size={12} /> Inloggen als
          </h4>
          <p className="text-xs text-ink/55 dark:text-white/55 mb-2">Genereert een 1-uur JWT — handeling wordt geaudit.</p>
          <button type="button" onClick={impersonateNow} disabled={busy}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-black/10 dark:border-white/15 hover:bg-black/[0.03] dark:hover:bg-white/5 text-xs font-semibold text-ink/75 dark:text-white/75 transition-colors disabled:opacity-40">
            Impersonate {user.email}
          </button>
        </div>
      </div>
    </Sheet>
  );
};

// ─── Action dialogs ────────────────────────────────────────────────────

const BulkCreditsDialog: React.FC<{ open: boolean; userIds: number[]; onClose: () => void; onDone: () => void }> = ({ open, userIds, onClose, onDone }) => {
  const [credits, setCredits] = useState(10);
  if (!open) return null;
  const run = async () => {
    if (userIds.length === 0) { toast.error('Geen gebruikers geselecteerd'); return; }
    try {
      const res = await adminApi.bulkCredits(userIds, credits);
      toast.success(`${res.updated} gebruiker(s) bijgewerkt (${credits >= 0 ? '+' : ''}${credits} credits)`);
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Bulk credits mislukt');
    }
  };
  return (
    <ConfirmDialog
      open={open}
      title={`${credits >= 0 ? '+' : ''}${credits} credits aan ${userIds.length} gebruiker(s)`}
      description={
        <div className="space-y-3">
          <p>Negatief bedrag trekt credits af. Geen idempotentie — meerdere klikken stapelt op.</p>
          <input type="number" value={credits} onChange={(e) => setCredits(Number(e.target.value))}
            className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/15 bg-white dark:bg-ink/60 dark:text-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 font-mono"
          />
        </div>
      }
      confirmLabel="Toepassen"
      onConfirm={run}
      onCancel={onClose}
    />
  );
};

const BulkDeleteDialog: React.FC<{ open: boolean; users: AdminUser[]; onClose: () => void; onDone: () => void }> = ({ open, users, onClose, onDone }) => {
  if (!open) return null;
  const run = async () => {
    if (users.length === 0) { toast.error('Geen gebruikers geselecteerd'); return; }
    try {
      const res = await adminApi.bulkDelete(users.map((u) => u.id));
      toast.success(`${res.deleted} gebruiker(s) gedeactiveerd`);
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Bulk delete mislukt');
    }
  };
  return (
    <ConfirmDialog
      open={open}
      destructive
      typedConfirmation="DELETE"
      title={`${users.length} gebruiker(s) verwijderen`}
      description={
        <div>
          <p>Soft-delete via <code className="font-mono">is_active = 0</code>. Properties en renders blijven staan, login wordt geblokkeerd.</p>
          <ul className="mt-2 max-h-32 overflow-y-auto text-xs space-y-0.5">
            {users.slice(0, 8).map((u) => <li key={u.id} className="font-mono">{u.email}</li>)}
            {users.length > 8 && <li className="italic">…en {users.length - 8} meer</li>}
          </ul>
        </div>
      }
      confirmLabel="Verwijder"
      onConfirm={run}
      onCancel={onClose}
    />
  );
};

const InviteDialog: React.FC<{ open: boolean; onClose: () => void; onDone: () => void }> = ({ open, onClose, onDone }) => {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState<Role>('medewerker');
  if (!open) return null;
  const run = async () => {
    if (!email) { toast.error('Email verplicht'); return; }
    try {
      await adminApi.invite({ email, role, company: company || undefined });
      toast.success(`Invite verzonden naar ${email}`);
      setEmail(''); setCompany('');
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Invite mislukt');
    }
  };
  return (
    <ConfirmDialog
      open={open}
      title="Nieuwe gebruiker uitnodigen"
      description={
        <div className="space-y-3">
          <input type="email" placeholder="email@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/15 bg-white dark:bg-ink/60 dark:text-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20" />
          <input type="text" placeholder="Bedrijf (optioneel)" value={company} onChange={(e) => setCompany(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/15 bg-white dark:bg-ink/60 dark:text-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20" />
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}
            className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/15 bg-white dark:bg-ink/60 dark:text-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20">
            <option value="medewerker">medewerker</option>
            <option value="beheerder">beheerder</option>
            <option value="superadmin">superadmin</option>
          </select>
        </div>
      }
      confirmLabel="Verstuur"
      onConfirm={run}
      onCancel={onClose}
    />
  );
};

const MergeDuplicateDialog: React.FC<{ open: boolean; onClose: () => void; onDone: () => void }> = ({ open, onClose, onDone }) => {
  const [email, setEmail] = useState('');
  const [dryRun, setDryRun] = useState(true);
  if (!open) return null;
  const run = async () => {
    if (!email) { toast.error('Email verplicht'); return; }
    try {
      const res = await adminApi.mergeDuplicate(email, dryRun);
      toast.success(dryRun ? 'Dry-run voltooid — bekijk console voor preview' : 'Merge voltooid');
      console.warn('[merge-duplicate]', res);
      if (!dryRun) onDone();
      else onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Merge mislukt');
    }
  };
  return (
    <ConfirmDialog
      open={open}
      destructive={!dryRun}
      typedConfirmation={!dryRun ? 'MERGE' : undefined}
      title="Duplicate user samenvoegen"
      description={
        <div className="space-y-3">
          <p>Hermerge oudste user-row (bron) naar nieuwste (doel). Reversibel via soft-delete.</p>
          <input type="email" placeholder="user@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/15 bg-white dark:bg-ink/60 dark:text-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 font-mono" />
          <label className="flex items-center gap-2 text-xs text-ink/65 dark:text-white/65">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="accent-accent" />
            Dry-run (alleen preview, geen schrijfacties)
          </label>
        </div>
      }
      confirmLabel={dryRun ? 'Preview' : 'Merge nu'}
      onConfirm={run}
      onCancel={onClose}
    />
  );
};

const ImpersonateDialog: React.FC<{ open: boolean; users: AdminUser[]; onClose: () => void }> = ({ open, users, onClose }) => {
  const [q, setQ] = useState('');
  const clerk = useClerk();
  if (!open) return null;
  const matches = q ? users.filter((u) => u.email.toLowerCase().includes(q.toLowerCase())).slice(0, 8) : [];

  const run = async (u: AdminUser) => {
    if (!window.confirm(`Impersonate ${u.email}? Deze handeling wordt geaudit.`)) return;
    try {
      const { token } = await adminApi.impersonate(u.id as number);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (clerk as any).client.signIn.create({ strategy: 'ticket', ticket: token });
      await clerk.setActive({ session: result.createdSessionId });
      onClose();
      window.location.href = '/';
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Impersonate mislukt');
    }
  };

  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center px-5 bg-ink/55 dark:bg-black/75 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="w-full max-w-md bg-white dark:bg-ink rounded-2xl border border-black/8 dark:border-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.4)] overflow-hidden">
        <div className="p-5">
          <h3 className="text-base font-semibold tracking-[-0.01em] text-ink dark:text-white flex items-center gap-2">
            <KeyRound size={14} /> Inloggen als gebruiker
          </h3>
          <input
            type="search"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            placeholder="Zoek op email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="mt-3 w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/15 bg-white dark:bg-ink/60 dark:text-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
          <ul className="mt-3 divide-y divide-black/[0.05] dark:divide-white/[0.06] max-h-60 overflow-y-auto">
            {matches.map((u) => (
              <li key={u.id}>
                <button type="button" onClick={() => run(u)} className="w-full text-left py-2 px-1 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded-md flex items-center justify-between text-sm">
                  <span className="text-ink dark:text-white truncate">{u.email}</span>
                  <span className="text-[11px] text-ink/55 dark:text-white/55 ml-2">{u.role}</span>
                </button>
              </li>
            ))}
            {q && matches.length === 0 && <li className="text-xs text-ink/45 dark:text-white/45 py-3 text-center">Geen resultaten</li>}
          </ul>
        </div>
        <div className="flex items-center justify-end px-5 py-3 bg-black/[0.02] dark:bg-white/[0.03] border-t border-black/8 dark:border-white/10">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md border border-black/10 dark:border-white/15 hover:bg-black/[0.03] dark:hover:bg-white/5 text-xs font-semibold text-ink/75 dark:text-white/75 transition-colors">
            Sluit
          </button>
        </div>
      </div>
    </div>
  );
};

// Suppress unused-import warnings for icons used inside dialogs in same file
void GitMerge;
